import { inject, Injectable } from '@angular/core';
import { gql } from '@apollo/client/core';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { ActionTypeEnum, Workflow, WorkflowInput } from '@app/core/model/entities/workflow/workflow';
import ApiService from '@services/api.service';
import { AppManager } from '@services/managers/app.manager';
import { plainToInstance } from 'class-transformer';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({providedIn: 'root'})
export class WorkflowsService {
  private readonly workflowInfoGraphqlFragment = gql`
    fragment WorkflowInfo on Workflow {
      id
      name
      organizationId
      service
      trigger
      entityType
      url
      config
      queryParams
      creationDate
      lastChangeDate
      lastExecutedAt
    }
  `;

  private readonly appManager = inject(AppManager);
  private readonly apiService = inject(ApiService);

  /**
   * Make an API request to fetch the number of remaining workflow tokens for the current organization.
   * @param organizationId ID of the organization for which to fetch the remaining workflow tokens.
   * @return Observable emitting the number of remaining workflow tokens.
   */
  public remainingWorkflowTokens(organizationId: number): Observable<number> {
    const query = gql`
      query RemainingWorkflowTokens($organizationId: String!) {
        remainingWorkflowTokens(organizationId: $organizationId)
      }
    `;
    const variables = {organizationId};
    return this.apiService.query({query, variables})
      .pipe(map(data => data['remainingWorkflowTokens'] as number));
  }

  /**
   * Make an API request to fetch the list of workflows based on current organization.
   *
   * @param entityType Target entity type for which workflows should be retrieved.
   * @param trigger Trigger type for which workflows should be retrieved.
   * @return Observable emitting a list of workflows to display.
   */
  public loadAllWorkflows(entityType?: EntityTypeEnum, trigger?: ActionTypeEnum): Observable<Workflow[]> {
    const query = gql`
      query Workflows($organizationId: String!, $entityType: EntityType, $trigger: WorkflowTrigger) {
        workflows(organizationId: $organizationId, entityType: $entityType, trigger: $trigger) {
          ...WorkflowInfo
        }
      }
      ${this.workflowInfoGraphqlFragment}
    `;
    const variables = {
      organizationId: this.appManager.currentOrganization.id,
      entityType,
      trigger
    };

    return this.apiService
      .query({query, variables})
      .pipe(map(data => plainToInstance(Workflow, data['workflows'] as Workflow[])));
  }

  /**
   * Execute a manual workflow with the given entity ids.
   * @param workflowId Identifier of the workflow to execute.
   * @param entityIds List of target entity ids (can be empty).
   * @return Observable emitting true if execution was triggered successfully.
   */
  public executeManualWorkflow(workflowId: number, entityIds: string[]): Observable<boolean> {
    const mutation = gql`
    mutation ExecuteManualWorkflow($workflowId: Int!, $entityIds: [String!]!) {
      runWorkflow(workflowId: $workflowId, entityIds: $entityIds)
    }
  `;

    const variables = {
      workflowId,
      entityIds
    };

    return this.apiService.mutate({mutation, variables}).pipe(
      map(data => data['executeManualWorkflow'])
    );
  }

  /**
   * Make an API request to create a new Workflow with the provided data.
   * @param workflowInput Workflow data.
   * @return Observable emitting the new Workflow that was created.
   */
  public createWorkflow(workflowInput: WorkflowInput): Observable<Workflow> {
    const mutation = gql`
      mutation CreateWorkflow($workflowInput: CreateWorkflowInput!) {
        createWorkflow(workflowInput: $workflowInput) {
          ...WorkflowInfo
        }
      }
      ${this.workflowInfoGraphqlFragment}
    `;
    const variables = {
      workflowInput: {
        organizationId: this.appManager.currentOrganization.id,
        ...workflowInput
      }
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(map(data => plainToInstance(Workflow, data['createWorkflow'])));
  }

  /**
   * Call the API to update a Workflow.
   * @param workflow Workflow to update.
   * @param workflowInput Data to update the Workflow with.
   * @return Observable emitting the updated Workflow once the update is completed.
   */
  public updateWorkflow(workflow: Workflow, workflowInput: WorkflowInput): Observable<Workflow> {
    const mutation = gql`
      mutation UpdateWork($workflowInput: UpdateWorkflowInput!) {
        updateWorkflow(workflowInput: $workflowInput) {
          ...WorkflowInfo
        }
      }
      ${this.workflowInfoGraphqlFragment}
    `;
    const variables = {
      workflowInput: {
        id: workflow.id,
        name: workflow.name,
        url: workflow.url,
        trigger: workflow.trigger,
        entityType: workflow.entityType,
        queryParams: workflow.queryParams,
        ...workflowInput
      }
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(map(data => plainToInstance(Workflow, data['updateWorkflow'])));
  }

  /**
   * Make an API request to delete Workflows.
   * @param workflows A list of Workflows to delete.
   * @return True if all Workflows have been deleted successfully, false otherwise.
   */
  public deleteWorkflows(workflows: Workflow[]): Observable<boolean> {
    const mutation = gql`
      mutation DeleteWorkflow($ids: [Int!]!) {
        deleteWorkflows(ids: $ids)
      }
    `;
    const variables = {ids: workflows.map(workflow => workflow.id)};

    return this.apiService.mutate({mutation, variables})
      .pipe(map(data => data['deleteWorkflows'] as boolean));
  }
}
